import type { TFunction } from "i18next";
import { ColorV2 } from "config/colors";
import { GigType } from "model/Gig";
import { ScheduleStatus } from "model/Schedule";

interface ResolveScheduleShiftStatusParams {
  readonly gigType?: string;
  readonly status: ScheduleStatus;
}

const teamPublicGigTypes: ReadonlySet<string> = new Set(
  [GigType.PUBLIC, GigType.WORKPOOL, GigType.WORKGROUP].map((type) =>
    type.toLowerCase(),
  ),
);

/** An undefined public/team shift is an unfilled slot in the schedule. */
export function resolveScheduleShiftStatus({
  gigType,
  status,
}: ResolveScheduleShiftStatusParams): ScheduleStatus {
  return status === ScheduleStatus.UNDEFINED &&
    teamPublicGigTypes.has(gigType?.toLowerCase() ?? "")
    ? ScheduleStatus.OPEN
    : status;
}

export interface BlockStatusStyling {
  backgroundColor: ColorV2;
  color: ColorV2;
  isStriped?: boolean;
}

/** Resolve background + text colors for a schedule status. */
export const getBlockStatusStyling = (
  status: ScheduleStatus,
): BlockStatusStyling => {
  switch (status) {
    case ScheduleStatus.ACCEPTED: {
      return {
        backgroundColor: ColorV2.SimflexityGreen,
        color: ColorV2.White,
      };
    }
    case ScheduleStatus.REPORTED: {
      return {
        backgroundColor: ColorV2.SimflexityGreen,
        color: ColorV2.White,
      };
    }
    case ScheduleStatus.SUBMITTED: {
      return {
        backgroundColor: ColorV2.SimflexityGreen,
        color: ColorV2.White,
      };
    }
    case ScheduleStatus.DECLINED: {
      return {
        backgroundColor: ColorV2.DeclinedRed,
        color: ColorV2.White,
      };
    }
    case ScheduleStatus.OPEN: {
      return {
        backgroundColor: ColorV2.NotFilledGrey,
        color: ColorV2.White,
      };
    }
    case ScheduleStatus.PENDING: {
      return {
        backgroundColor: ColorV2.OfferedYellow,
        color: ColorV2.Blue1,
      };
    }
    case ScheduleStatus.OFFERED: {
      return {
        backgroundColor: ColorV2.OfferedYellow,
        color: ColorV2.Blue1,
      };
    }
    case ScheduleStatus.PLANNED_ABSENCE: {
      return {
        backgroundColor: ColorV2.AbsenceLightBlue,
        color: ColorV2.Blue1,
      };
    }
    case ScheduleStatus.UNDEFINED: {
      return {
        backgroundColor: ColorV2.NotFilledGrey,
        color: ColorV2.White,
      };
    }
    case ScheduleStatus.UNPLANNED_ABSENCE: {
      return {
        backgroundColor: ColorV2.UnplannedAbsenceYellow,
        color: ColorV2.Blue1,
      };
    }
    case ScheduleStatus.WITHDRAWN: {
      return {
        backgroundColor: ColorV2.WithdrawnPink,
        color: ColorV2.Blue1,
      };
    }
    case ScheduleStatus.NOT_AVAILABLE: {
      return {
        backgroundColor: ColorV2.White,
        color: ColorV2.Blue1,
        isStriped: true,
      };
    }
    default: {
      return {
        backgroundColor: ColorV2.NotFilledGrey,
        color: ColorV2.White,
      };
    }
  }
};

/** Resolve the translated label for a schedule status. */
export const getBlockStatusText = (
  status: ScheduleStatus,
  t: TFunction,
): string => {
  switch (status) {
    case ScheduleStatus.ACCEPTED: {
      return t("Schedule.Booked");
    }
    //To handle timereports
    case ScheduleStatus.REPORTED: {
      return t("Schedule.Booked");
    }
    //To handle timereports
    case ScheduleStatus.SUBMITTED: {
      return t("Schedule.Booked");
    }
    case ScheduleStatus.DECLINED: {
      return t("Schedule.Declined");
    }
    case ScheduleStatus.OPEN: {
      return t("Schedule.Notfilled");
    }
    case ScheduleStatus.PENDING: {
      return t("Schedule.Pending");
    }
    case ScheduleStatus.OFFERED: {
      return t("Schedule.Offered");
    }
    case ScheduleStatus.PLANNED_ABSENCE: {
      return t("Schedule.PlannedAbsence");
    }
    case ScheduleStatus.UNDEFINED: {
      return t("Schedule.Undefined");
    }
    case ScheduleStatus.UNPLANNED_ABSENCE: {
      return t("Schedule.UnplannedAbsence");
    }
    case ScheduleStatus.WITHDRAWN: {
      return t("Schedule.Withdrawn");
    }
    case ScheduleStatus.NOT_AVAILABLE: {
      return t("Schedule.NotAvailable");
    }
    default: {
      return t("Schedule.Notfilled");
    }
  }
};

/** Resolve label + description text for the shift details status banner. */
export const getShiftDetailTexts = (
  status: ScheduleStatus,
  t: TFunction,
  interpolation: Record<string, string> = {},
): { label: string; text: string } => {
  switch (status) {
    case ScheduleStatus.ACCEPTED: {
      return {
        label: t("Schedule.Booked"),
        text: t("Schedule.BookedStatusText"),
      };
    }
    case ScheduleStatus.REPORTED: {
      return {
        label: t("Schedule.Booked"),
        text: t("Schedule.BookedStatusText"),
      };
    }
    case ScheduleStatus.SUBMITTED: {
      return {
        label: t("Schedule.Booked"),
        text: t("Schedule.BookedStatusText"),
      };
    }
    case ScheduleStatus.DECLINED: {
      return {
        label: t("Schedule.Declined"),
        text: t("Schedule.DeclinedStatusText"),
      };
    }
    case ScheduleStatus.OPEN: {
      return {
        label: t("Schedule.Notfilled"),
        text: t("Schedule.NotfilledStatusText"),
      };
    }
    case ScheduleStatus.PENDING: {
      return {
        label: t("Schedule.Pending"),
        text: t("Schedule.PendingStatusText"),
      };
    }
    case ScheduleStatus.OFFERED: {
      return {
        label: t("Schedule.Offered"),
        text: t("Schedule.OfferedStatusText"),
      };
    }
    case ScheduleStatus.PLANNED_ABSENCE: {
      return {
        label: t("Schedule.PlannedAbsence"),
        text: t("Schedule.PlannedAbsenceStatusText"),
      };
    }
    case ScheduleStatus.UNDEFINED: {
      return {
        label: t("Schedule.Notfilled"),
        text: t("Schedule.NotfilledStatusText"),
      };
    }
    case ScheduleStatus.UNPLANNED_ABSENCE: {
      return {
        label: t("Schedule.UnplannedAbsence"),
        text: t("Schedule.UnplannedAbsenceStatusText"),
      };
    }
    case ScheduleStatus.WITHDRAWN: {
      return {
        label: t("Schedule.Withdrawn"),
        text: t("Schedule.WithdrawnStatusText"),
      };
    }
    case ScheduleStatus.NOT_AVAILABLE: {
      return {
        label: t("Schedule.NotAvailable"),
        text: t("Schedule.NotAvailableStatusText", interpolation),
      };
    }
    default: {
      return {
        label: t("Schedule.Notfilled"),
        text: t("Schedule.NotfilledStatusText"),
      };
    }
  }
};
